<template>
	<div class="customiser">
		<div class="introduction">
			<h1 class="heading">{{ rangeTitle }} radiator <span class="no-print">options</span></h1>
			<a :href="rangeUrl" class="button button--primary">Back to range</a>
		</div>

		<div class="content">
			<div class="column" id="renderColumn">
				<Render v-if="!visualiserOverlay" :viewProp="renderSettings.view" :zoomProp="renderSettings.zoom" :backgroundProp="renderSettings.background" :renderCodeProp="renderCode" :finishProp="custom.finish" @renderSettingsChanged="onRenderSettingsChanged" @radiatorRenderError="onRadiatorRenderError" />
				<Visualiser v-if="visualiserOverlay" :backgroundProp="custom.finish" :overlayProp="visualiserOverlay" />
			</div>

			<div class="column no-print">
				<FinishPicker :finishGroups="finishGroups" :finishSelectedProp="custom.finish" @finishSelected="onFinishSelected"/>
				<ValvePicker :valveGroups="valveGroups" :valveSelectedProp="custom.valve" @valveSelected="onValveSelected" :supplementaryHeatersProp="custom.suppHeaters" :supplementaryHeaterSelectedProp="custom.suppHeater" @supplementaryHeaterSelected="onSupplementaryHeaterSelected" />
				<AccessoryPicker :if="accessoryGroup.length" :accessoryGroupProp="accessoryGroup" @accessorySelected="onAccessorySelected"
				@accessoryRemoved="onAccessoryRemoved" />
			</div>
		</div>
		<div class="details">
			<Summary :productProp="productTitle" :rangeProp="rangeTitle" :finishSelectedProp="custom.finish" :valveSelectedProp="custom.valve" :supplementaryHeaterProp="custom.suppHeater" :accessoriesSelectedProp="custom.accessories" 
			:accessoriesQuantitiesProp="custom.accessoriesQuantities"
			@accessoryRemoved="onAccessoryRemoved"
			@accessoryQtyChanged="onAccessoryQuantityChange" />
			<CallToActionGroup :customUrlProp="url" :configProp="urlHash" :elementIdProp="elementId" :finishIdProp="custom.finish.id" :valveIdProp="custom.valve.id" :suppHeaterProp="custom.suppHeater ? 1 : 0" :viewProp="renderSettings.view" :zoomProp="renderSettings.zoom" :backgroundProp="renderSettings.background" :dealerAreaLinkProp="dealerAreaLink" />
			<TechnicalDetails :technicalDetailsProp="technicalDetails" />
			<AdditionalInformation :additionalInformationProp="additionalInformation" />
		</div>
		<Disclaimer :isOpenProp="disclaimer.isOpen" :copyProp="disclaimer.copy" @disclaimerClosed="onDisclaimerClosed"/>
	</div>
</template>

<script>
	import Render from './Render.vue';
	import FinishPicker from './FinishPicker.vue';
	import ValvePicker from './ValvePicker.vue';
	import AccessoryPicker from './AccessoryPicker.vue';
	import Summary from './Summary.vue';
	import CallToActionGroup from './CallToActionGroup.vue';
	import Visualiser from './Visualiser.vue';
	import Disclaimer from './Disclaimer.vue';
	import TechnicalDetails from './TechnicalDetails.vue';
	import AdditionalInformation from './AdditionalInformation.vue';

	// set up some constants
	const BASEURL = "https://s3-eu-west-1.amazonaws.com/bisque-media/renders/";
	const SEPARATOR = ".";

	export default {
		props: {
			productTitle: {
				type: String
			},
			rangeTitle: {
				type: String
			},
			rangeUrl: {
				type: String
			},
			elementId: {
				type: Number
			},
			finishId: {
				type: Number
			},
			valveId: {
				type: Number
			},
			visualiserOverlay: {
				type: String
			},
			renderCode: {
				type: String
			},
			dealerAreaLink: {
				type: null
			},
		},
		data() {
			return {
				radiatorId: '0',
				renderId: '0',
				custom: {
					finish: {
						id: 0,
						price: 0,
						pricePlusVat: 0,
						surcharge: 0,
						surchargePlusVat: 0,
						renderReference: '',
						thumbnail: '',
						title: '',
					},
					valve: {
						id: 0,
						price: 0,
						pricePlusVat: 0,
						surcharge: 0,
						surchargePlusVat: 0,
						renderReference: '',
						thumbnail: '',
						title: '',
					},
					accessories: [],
					accessoriesQuantities: [],
					suppHeaters: [],
					suppHeater: null,
				},
				renderSettings: {
					zoom: 1,
					background: '518ea8',
					view: 'Front',
				},
				finishGroups: [],
				valveGroups: [],
				accessoryGroup: [],
				disclaimer: {
					isOpen: false,
					copy: ''
				},
				technicalDetails: [],
				additionalInformation: []
			};
		},
		components: {
			Render,
			Summary,
			CallToActionGroup,
			FinishPicker,
			ValvePicker,
			AccessoryPicker,
			Visualiser,
			Disclaimer,
			TechnicalDetails,
			AdditionalInformation
		},
		beforeMount: function () {
			this.setFinishGroups(customiserApp.finishGroups);
			this.setValveGroups(customiserApp.valveGroups);
			this.setAccessoryGroup(customiserApp.accessories);
			if (customiserApp.supplementaryHeater) {
				this.setSupplementaryHeater(customiserApp.supplementaryHeater);
			}
			if (customiserApp.supplementaryHeaters) {
				// this.setSupplementaryHeater(customiserApp.supplementaryHeaters[0]);
				this.custom.suppHeaters = customiserApp.supplementaryHeaters;
			}
			if (location.hash) {
				this.resolveUrlHash();
			} else {
				this.setDefaultFinish();
			}
			this.setTechnicalDetails(customiserApp.technicalDetails);
			this.setAdditionalInformation(customiserApp.additionalInformation)
		},
		methods: {
			setFinishGroups: function(newFinishGroups) {
				this.finishGroups = newFinishGroups;
			},
			setDefaultFinish: function() {
				this.custom.finish = this.finishGroups[0].finishes[0];
				this.updateHash();
			},
			onFinishSelected: function(finish) {
				this.custom.finish = finish;
				this.updateHash();

				if (this.finishHasDisclaimer) {
					this.setDisclaimerCopy(this.custom.finish.disclaimer);
					this.disclaimer.isOpen = this.finishHasDisclaimer;
				}
			},
			setValveGroups: function(newValveGroups) {
				this.valveGroups = newValveGroups;
			},
			onValveSelected: function(valve) {
				this.custom.valve = valve;
				this.updateHash();
				
				if (this.valveHasDisclaimer) {
					this.setDisclaimerCopy(this.custom.valve.disclaimer);
					this.disclaimer.isOpen = this.valveHasDisclaimer;
				}
			},
			setSupplementaryHeater: function(supplementaryHeater) {
				this.custom.suppHeater = supplementaryHeater;
				this.updateHash();
			},
			onSupplementaryHeaterSelected: function(selected) {
				this.custom.suppHeater = selected;
				this.updateHash();
			},
			setAccessoryGroup: function(newAccessories) {
				this.accessoryGroup = newAccessories;
			},
			setTechnicalDetails: function(technicalDetails) {
				this.technicalDetails = technicalDetails;
			},
			setAdditionalInformation: function(additionalInformation) {
				this.additionalInformation = additionalInformation
			}, 
			onAccessorySelected: function(accessory) {
				// console.log('FOO');
				if (this.accessoryIsSelected(accessory)) return;
				// console.log('BAR');

				this.custom.accessoriesQuantities.push({
					id: accessory.id,
					qty: 1
				});
				accessory.qty = 1;

				this.custom.accessories.push(accessory);

				this.updateHash();
			},
			onAccessoryRemoved: function(subjectAccessory) {
				if (!this.accessoryIsSelected(subjectAccessory)) return;
				let newAccessoryList = [];
				let newAccessoryQtyList = [];

				for (let i = 0; i < this.custom.accessories.length; i++) {
					if (this.custom.accessories[i].id === subjectAccessory.id) continue;
					newAccessoryList.push(this.custom.accessories[i]);
				}

				for (let i = 0; i < this.custom.accessoriesQuantities.length; i++) {
					if (this.custom.accessoriesQuantities[i].id === subjectAccessory.id) continue; // Ignore removed accessory
					newAccessoryQtyList.push(this.custom.accessoriesQuantities[i]); // Add maintained accessories to new list (this feels really bulky... slim down!)
				}

				this.custom.accessories = newAccessoryList;
				this.custom.accessoriesQuantities = newAccessoryQtyList;
				
				this.updateHash();
			},
			accessoryIsSelected: function(subjectAccessory) {
				return this.custom.accessories.some(function(accessory) {
					return accessory === subjectAccessory;
				});
			},
			onRenderSettingsChanged: function(settings) {
				this.renderSettings = settings;
				this.updateHash();
			},
			updateHash: function() {
				location.href = location.href.replace(location.hash,"") + this.urlHash;
			},
			resolveUrlHash: function() {
				let loadOut = location.hash.replace('#', '').split(SEPARATOR);
				this.custom.finish = this.getFinishById(loadOut[0]);
				if (loadOut[1] != '0') this.custom.valve = this.getValveById(loadOut[1]);
				if (loadOut[2] != '0') this.custom.suppHeater = this.getSuppHeaterById(loadOut[2]);

				if (loadOut[7] && loadOut[7] != '0') this.custom.accessoriesQuantities = this.setAccessoriesQuantities(loadOut[7].split(','));
				if (loadOut[3] != '0') this.custom.accessories = this.getAccesoriesByIds(loadOut[3].split(','));

				this.renderSettings.view = loadOut[4];
				this.renderSettings.zoom = loadOut[5];
				this.renderSettings.background = loadOut[6];
			},
			getFinishById: function(id) {
				for (let i = 0; i < this.finishGroups.length; i++) {
					let finishGroup = this.finishGroups[i];
					for (let x = 0; x < finishGroup.finishes.length; x++) {
						let finish = finishGroup.finishes[x];
						if (finish.id == id) return finish;
					}
				}
			},
			getValveById: function(id) {
				for (let i = 0; i < this.valveGroups.length; i++) {
					let valveGroup = this.valveGroups[i];
					for (let x = 0; x < valveGroup.valveSets.length; x++) {
						let valveSet = valveGroup.valveSets[x];
						for (let y = 0; y < valveSet.finishes.length; y++) {
							let valve = valveSet.finishes[y];
							if (valve.id == id) return valve;
						}
					}
				}
			},
			getSuppHeaterById: function(id) {
				console.log(this.custom.suppHeaters);
				for (let i = 0; i < this.custom.suppHeaters.length; i++) {
					let supplementaryHeater = this.custom.suppHeaters[i];
					if (supplementaryHeater.id == id) return supplementaryHeater;
				}
			},
			getAccesoriesByIds: function(ids) {
				let accessories = [];
				ids.forEach(id => accessories.push(this.getAccessoryById(id)));
				return accessories;
			},
			getAccessoryById: function(id) {
				return this.accessoryGroup.find(accessory => accessory.id == id);
			},
			onDisclaimerClosed: function() {
				this.disclaimer.isOpen = false;
				this.disclaimer.copy = '';
			},
			onRadiatorRenderError: function(event) {
				this.custom.finish.renderReference = this.finishGroups[0].finishes[0].renderReference;
			},
			setDisclaimerCopy: function(copy) {
				if (copy) {
					this.disclaimer.copy = copy;
				}
			},
			setAccessoriesQuantities: function(accessories) {
				let accessoriesQtys = [];

				accessories.forEach((accessory) => {
					let arr = accessory.split('-');
					let accessoryId = parseInt(arr[0]);
					let accessoryQty = parseInt(arr[1]);
					
					accessoriesQtys.push({ id: accessoryId, qty: accessoryQty });
				});

				return accessoriesQtys;
			},
			onAccessoryQuantityChange: function(accessory) {
				this.custom.accessoriesQuantities.forEach((oldAccessory) => {
					if (oldAccessory.id === accessory.id) {
						oldAccessory.qty = accessory.qty;
					}
				})

				this.updateHash();
			}
		},
		computed: {
			urlHash: function() {
				let accessoryIds = this.custom.accessories.map((accessory) => accessory.id);
				let accessoriesQtys = this.custom.accessoriesQuantities.map((accessory) => {
					return accessory.id + '-' + accessory.qty
				});

				return `#${
					this.custom.finish.id + SEPARATOR +
					this.custom.valve.id + SEPARATOR +
					(this.custom.suppHeater ? this.custom.suppHeater.id : 0) + SEPARATOR +
					(accessoryIds.length ? accessoryIds.join(',') : 0) + SEPARATOR +
					this.renderSettings.view + SEPARATOR +
					this.renderSettings.zoom + SEPARATOR +
					this.renderSettings.background + SEPARATOR + 
					(accessoriesQtys.length ? accessoriesQtys.join(',') : 0)
				}`;
			},
			legacyUrlHash: function() {
				//HASH: ID + finishID + renderID + colourID + heatControl + pipeDirection + valveID + valveLetter + valveFinish
				let hash = '#';
				hash = hash + this.radiatorId;
				hash = hash + SEPARATOR + this.custom.finish.renderReference;
				hash = hash + SEPARATOR + this.renderID; // MAT
				hash = hash + SEPARATOR + this.custom.finish.renderReference; // Colour
				hash = hash + SEPARATOR + 0; // Heat Control
				hash = hash + SEPARATOR + 'FF'; // Pipe Direction
				hash = hash + SEPARATOR + this.custom.valve.renderReference; // Valve ID
				hash = hash + SEPARATOR + this.custom.valve.renderReference; // Valve Letter
				hash = hash + SEPARATOR + this.custom.valve.renderReference; // Valve Finish
				hash = hash + SEPARATOR + this.renderSettings.background; // Background
				hash = hash + SEPARATOR + this.renderSettings.view; // View
				hash = hash + SEPARATOR + (this.custom.suppHeater ? 1 : 0); // Supplementery Heater
				let accessoryIds = this.custom.accessories.map((accessory) => accessory.id);
				hash = hash + SEPARATOR + (accessoryIds.length ? accessoryIds.join(',') : 0); // Accessories

				// Accessory Quantities
				let accessoriesQtys = this.custom.accessoriesQuantities.map((accessory) => {
					return accessory.id + '-' + accessory.qty
				})
				hash = hash + SEPARATOR + (accessoriesQtys.length ? accessoriesQtys.join(',') : 0);
				// hash = hash + SEPARATOR + this.renderSettings.zoom; // Zoom
				return hash;
			},
			url: function() {
				//return window.location.origin + window.location.path + this.urlHash;
				return window.location.href;
			},
			finishHasDisclaimer: function() {
				return this.custom.finish.disclaimer ? true : false;
			},
			valveHasDisclaimer: function() {
				return this.custom.valve.disclaimer ? true : false
			}
		}
	};
</script>

<style>
	.customiser {
		width: 100%;
		max-width: 100%;
	}

	.content {
		display: flex;
		justify-content: space-between;
		flex-direction: column;
		align-items: flex-start;
		gap: 2rem;
	}

	#renderColumn {
		position: static;
		top: 4rem;
	}

	@media screen and (min-width: 1024px) {
		.content {
			flex-direction: row;
		}

		.column {
			width: 50%;
		}

		#renderColumn {
			position: sticky;
		}
	}

	.introduction {
		display: flex;
		flex-direction: column;
		text-align: center;
		margin-bottom: 2rem;
	}

	@media (min-width: 920px) {
		.introduction {
			flex-direction: row;
			gap: 1rem;
			justify-content: space-between;
			align-items: baseline;
			margin-bottom: 0;
		}
	}
</style>
